import { useContext, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Fab,
  FormControl,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import HistoryIcon from '@mui/icons-material/History';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { v4 as uuidv4 } from 'uuid';
import UserContext from '../../context/user';
import { useAiPanel } from '../../context/aiPanel';
import useAiApiRoutes from '../../hooks/api/useAiApiRoutes';
import AiChatView from './AiChatView';
import { AiChatMessage } from './sseProtocol';
import { AiChatConfig, AiConversationSummary } from '../../../types';

const BRAND = '#0088CF';
export const AI_PANEL_WIDTH = 440;

const formatTime = (value: string | null) => (value ? new Date(value).toLocaleString() : '');

// Compact history list for the panel — clicking a conversation resumes it.
const PanelHistory = ({ onResume }: { onResume: (uuid: string, messages: AiChatMessage[]) => void }) => {
  const { getHistory, getConversation } = useAiApiRoutes();
  const [conversations, setConversations] = useState<AiConversationSummary[] | null>(null);

  useEffect(() => {
    getHistory().then((result) => setConversations(result?.conversations ?? []));
  }, [getHistory]);

  const resume = async (id: string) => {
    const detail = await getConversation(id);
    if (detail) {
      onResume(
        detail.uuid,
        detail.messages.map((m) => ({ role: m.role, content: m.content, truncated: m.truncated }))
      );
    }
  };

  if (conversations === null) {
    return (
      <Box textAlign="center" mt={4}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (conversations.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        gap={1}
        color="text.disabled"
      >
        <ForumOutlinedIcon sx={{ fontSize: 40 }} />
        <Typography variant="body2" color="text.secondary">
          No conversations yet.
        </Typography>
      </Box>
    );
  }
  return (
    <List dense disablePadding sx={{ overflowY: 'auto' }}>
      {conversations.map((conversation, index) => (
        <Box key={conversation.id}>
          {index > 0 ? <Divider component="li" /> : null}
          <ListItemButton onClick={() => resume(conversation.id)} sx={{ py: 1.25 }}>
            <ListItemAvatar sx={{ minWidth: 42 }}>
              <Avatar sx={{ width: 30, height: 30, bgcolor: 'rgba(0,136,207,0.12)' }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 15, color: BRAND }} />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={conversation.preview || '(no messages)'}
              primaryTypographyProps={{ noWrap: true, fontSize: 14 }}
              secondary={`${conversation.messageCount} messages · ${formatTime(conversation.lastMessageAt)}`}
              secondaryTypographyProps={{ noWrap: true, fontSize: 12 }}
            />
          </ListItemButton>
        </Box>
      ))}
    </List>
  );
};

// The StackIQ side panel: an in-flow left column that pushes the app aside so
// the user can chat while working. Content stays mounted while closed, so an
// in-progress conversation survives close/reopen and page navigation.
export const AiPanel = () => {
  const { isOpen, closePanel } = useAiPanel();
  const { user, isLoggedIn, constants } = useContext(UserContext);
  const { getChatConfig } = useAiApiRoutes();
  const chatEnabled = constants.aiChatEnabled === true;

  const isAdmin = Boolean(user?.roles?.includes('ADMIN'));
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [owners, setOwners] = useState<{ id: string; name: string }[]>([]);
  const [scope, setScope] = useState<string | null>(null);
  const [chatKey, setChatKey] = useState(0);
  const [conversationId, setConversationId] = useState(() => uuidv4());
  const [seedMessages, setSeedMessages] = useState<AiChatMessage[] | undefined>(undefined);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    if (!isOpen || configLoaded || !isLoggedIn) return;
    getChatConfig().then((config: AiChatConfig | undefined) => {
      if (config && config.admin) {
        setOwners(config.owners);
        setScope('all');
      }
      setConfigLoaded(true);
    });
  }, [isOpen, configLoaded, isLoggedIn, getChatConfig]);

  const aiEnabled = Boolean(user?.aiEnabled || isAdmin);

  const newChat = () => {
    setConversationId(uuidv4());
    setSeedMessages(undefined);
    setChatKey((key) => key + 1);
    setView('chat');
  };

  const resume = (uuid: string, messages: AiChatMessage[]) => {
    setConversationId(uuid);
    setSeedMessages(messages);
    setChatKey((key) => key + 1);
    setView('chat');
  };

  const changeScope = (next: string) => {
    setScope(next);
    newChat(); // fresh conversation when the data scope changes
  };

  // Switched off for this environment: render nothing at all, so the panel
  // takes no layout width and the app sits full-bleed as it did before.
  if (!chatEnabled) return null;

  return (
    <Box
      sx={{
        width: isOpen ? { xs: '100vw', sm: AI_PANEL_WIDTH } : 0,
        transition: 'width 225ms ease',
        overflow: 'hidden',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: isOpen ? '1px solid' : 'none',
        borderColor: 'divider',
        background: '#fff',
      }}
    >
      <Box sx={{ width: { xs: '100vw', sm: AI_PANEL_WIDTH }, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Brand-gradient header, matching the app's navbar */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={1.75}
          py={1.25}
          sx={{ background: 'linear-gradient(90.07deg, #0088CF 0%, #00B7B2 100%)' }}
        >
          <Box display="flex" alignItems="center" gap={1.25}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.22)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#fff', lineHeight: 1.15, fontWeight: 700, letterSpacing: 0.2 }}>
                StackIQ
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.1, fontSize: 11 }}>
                {isAdmin ? 'Cross-account assistant' : 'Your container data assistant'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.9)' } }}>
            <Tooltip title={view === 'history' ? 'Back to chat' : 'History'}>
              <IconButton
                size="small"
                onClick={() => setView(view === 'history' ? 'chat' : 'history')}
                aria-label="Conversation history"
                sx={view === 'history' ? { background: 'rgba(255,255,255,0.22)' } : undefined}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="New chat">
              <IconButton size="small" onClick={newChat} aria-label="New chat">
                <AddCommentOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close panel">
              <IconButton size="small" onClick={closePanel} aria-label="Close StackIQ panel">
                <KeyboardDoubleArrowLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {isAdmin ? (
          // Scope selector lives inside the gradient header block as a
          // translucent control, not a detached form field below it.
          <Box
            px={1.75}
            pb={1.25}
            pt={0.25}
            sx={{ background: 'linear-gradient(90.07deg, #0088CF 0%, #00B7B2 100%)' }}
          >
            <FormControl size="small" fullWidth>
              <Select
                value={scope ?? 'all'}
                onChange={(e) => changeScope(e.target.value)}
                displayEmpty
                aria-label="Chatting with account scope"
                renderValue={(value) => (
                  <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', flexShrink: 0 }}>
                      Chatting with
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }} noWrap>
                      {value === 'all' ? 'All accounts' : owners.find((o) => o.id === value)?.name || 'Account'}
                    </Typography>
                  </Box>
                )}
                sx={{
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.16)',
                  transition: 'background 150ms ease',
                  '&:hover': { background: 'rgba(255,255,255,0.24)' },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.85)' },
                  '& .MuiSelect-select': { py: 0.75 },
                }}
              >
                <MenuItem value="all">All accounts</MenuItem>
                <Divider />
                {owners.map((owner) => (
                  <MenuItem key={owner.id} value={owner.id}>
                    {owner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ) : null}

        <Box flex={1} minHeight={0} display="flex" flexDirection="column">
          {view === 'history' ? (
            <PanelHistory onResume={resume} />
          ) : (
            <AiChatView
              key={chatKey}
              flat
              hideHeader
              scope={isAdmin ? scope ?? 'all' : null}
              conversationId={conversationId}
              seedMessages={seedMessages}
              disabled={!aiEnabled}
              disabledReason="Ask VirtualStack to enable StackIQ for your account"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Floating launcher, bottom-left on every page while the panel is closed.
export const AiPanelLauncher = () => {
  const { isOpen, openPanel } = useAiPanel();
  const { user, isLoggedIn, constants } = useContext(UserContext);

  const isAdmin = Boolean(user?.roles?.includes('ADMIN'));
  // Explicit true only: while constants are still loading we say nothing,
  // rather than flashing a companion that may be switched off.
  const chatEnabled = constants.aiChatEnabled === true;
  const available = chatEnabled && isLoggedIn && (isAdmin || Boolean(user?.aiEnabled));
  if (!available || isOpen) return null;

  return (
    <Tooltip title="Ask StackIQ" placement="right">
      <Fab
        size="medium"
        onClick={openPanel}
        aria-label="Open StackIQ"
        sx={{
          position: 'fixed',
          left: 20,
          bottom: 76,
          zIndex: (theme) => theme.zIndex.drawer,
          color: '#fff',
          background: 'linear-gradient(135deg, #0088CF, #00B7B2)',
          boxShadow: '0 4px 14px rgba(0, 136, 207, 0.4)',
          '&:hover': { background: 'linear-gradient(135deg, #0077b5, #009e9a)' },
        }}
      >
        <AutoAwesomeIcon />
      </Fab>
    </Tooltip>
  );
};

export default AiPanel;
